import { useMemo, useState } from 'react';

import type { ThemeListItem } from '@playerbase/types';
import { Tooltip, cn } from '@playerbase/ui';

// Searchable picker rendered inside the pattern card's actions popover
// when the studio chooses "Merge into…". Lists every other active theme
// in the same game; selecting one fires the merge RPC with the current
// card as the loser and the picked theme as the winner.
//
// Stateless beyond the inline search filter - the parent owns the
// popover open/close lifecycle so the menu / picker views can share one
// anchor without flicker.

export interface MergeThemePickerProps {
  candidates: ThemeListItem[];
  // The card the picker was opened from; excluded from the list so the
  // studio cannot merge a theme into itself.
  excludeId: string;
  onSelect: (winnerThemeId: string) => void;
  onCancel: () => void;
  // Optional message rendered above the list. Used by the parent to
  // surface errors from a previous merge attempt without re-mounting.
  errorMessage?: string | null;
  // Disables the option buttons during the in-flight RPC.
  busy?: boolean;
}

// Matches the pattern-card polarity dot vocabulary so the picker items
// read the same way as the cards they map onto.
function polarityDotClass(polarity: ThemeListItem['dominantPolarity']): string {
  if (polarity === 'positive') return 'bg-teal';
  if (polarity === 'negative') return 'bg-danger';
  return 'bg-text-muted';
}

export function MergeThemePicker({
  candidates,
  excludeId,
  onSelect,
  onCancel,
  errorMessage = null,
  busy = false,
}: MergeThemePickerProps): JSX.Element {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const eligible = candidates.filter((c) => c.id !== excludeId);
    const matched =
      q === '' ? eligible : eligible.filter((c) => c.title.toLowerCase().includes(q));
    // Most-mentioned first so the canonical target (the one most likely
    // to be the winner) sits at the top of the list. Tie-break on title
    // for stable ordering between renders.
    return [...matched].sort((a, b) => {
      if (b.memberCount !== a.memberCount) return b.memberCount - a.memberCount;
      return a.title.localeCompare(b.title);
    });
  }, [candidates, excludeId, query]);

  return (
    <div className="flex w-72 flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-text-muted">
          Merge into
        </span>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-text-muted hover:text-text"
        >
          Cancel
        </button>
      </div>
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search patterns…"
        className="w-full rounded-md border border-border bg-surface px-2 py-1 text-sm text-text placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        autoFocus
      />
      {errorMessage !== null && (
        <p className="text-xs text-danger-text">{errorMessage}</p>
      )}
      <div className="no-scrollbar max-h-64 overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="px-1 py-2 text-xs text-text-muted">
            No other patterns to merge into.
          </p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {filtered.map((c) => (
              <li key={c.id} className="min-w-0">
                <Tooltip
                  delayMs={500}
                  multiline
                  className="w-full"
                  content={
                    <span className="flex flex-col gap-1 text-left">
                      <span className="font-semibold text-text">{c.title}</span>
                      {c.description !== null && (
                        <span className="text-text-muted">{c.description}</span>
                      )}
                    </span>
                  }
                >
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onSelect(c.id)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm text-text hover:bg-surface-3 disabled:cursor-not-allowed disabled:opacity-60',
                    )}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        'inline-block h-2 w-2 shrink-0 rounded-full',
                        polarityDotClass(c.dominantPolarity),
                      )}
                    />
                    <span className="min-w-0 flex-1 truncate">{c.title}</span>
                    <span className="shrink-0 text-xs text-text-muted">
                      {c.memberCount} mention{c.memberCount === 1 ? '' : 's'}
                    </span>
                  </button>
                </Tooltip>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
